import { useCallback, useRef, useState } from "react";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";

const fetchOwnerId = async (sessionId) => {
  const { educator_id: ownerId } = await request(`feedback/${sessionId}/surveys`, { method: "GET" });
  if (!ownerId) throw new Error("The session's group names no educator");
  return ownerId;
};

const fetchTeacherRow = async (sessionId, ownerId) => {
  const { sessions } = await request(
    `account/session_history?session_id=${sessionId}&educator_id=${ownerId}`,
    { method: "GET" },
  );
  if (!sessions?.length) throw new Error("The owner's session history has no row for this session");
  return sessions[0];
};

// The session row the session's own teacher gets from their Session Reports,
// read on the owner's behalf. Without it the report falls back to what the
// Session Analyzer always rendered, so a failure here never fails the check.
export const useSessionRow = () => {
  const [ownerId, setOwnerId] = useState(null);
  const [sessionRow, setSessionRow] = useState(null);
  const [isLoadingRow, setIsLoadingRow] = useState(false);

  // The session the latest load was started for. An answer for any other
  // session arrived after the admin moved on, and is dropped.
  const latestSessionId = useRef(null);

  const loadSessionRow = useCallback(async (sessionId) => {
    latestSessionId.current = sessionId;
    const isLatest = () => latestSessionId.current === sessionId;
    setIsLoadingRow(true);
    try {
      const owner = await fetchOwnerId(sessionId);
      const row = await fetchTeacherRow(sessionId, owner);
      if (!isLatest()) return;
      setOwnerId(owner);
      setSessionRow(row);
    } catch (e) {
      logError("Admin session analyzer could not load the session row", e, {
        category: LogCategory.ERROR,
        sessionId,
      });
      if (!isLatest()) return;
      setOwnerId(null);
      setSessionRow(null);
    } finally {
      if (isLatest()) setIsLoadingRow(false);
    }
  }, []);

  return { ownerId, sessionRow, isLoadingRow, loadSessionRow };
};
